/**
 * Role-access route metadata.
 *
 * The V2 shell has been retired; the `v2-*` IDs remain stable permission keys
 * persisted by existing tenants. Paths are matched against both historical
 * /v2 bookmarks and the canonical legacy-only application routes.
 */

import {
  normalizeRole,
  roleCanAccessPage,
} from './roleAccess';
import { OPERATIONAL_ASSURANCE_READ_ROLES } from './operationalAssuranceAccess';
import { legacyPathForV2Path } from './legacyRouteCompatibility';

// Stable permission/catalog groups used by the Users & Accesses surface.
//
// Insights Avançados was promoted from secondary/collapsible -> primary so it
// sits right after Qualidade with the same pilar treatment.
export const V2_NAV_GROUPS = [
  { id: 'ops-home', label: 'Centro Operacional', tier: 'primary' },
  { id: 'planning', label: 'Planejamento', tier: 'primary' },
  { id: 'safety', label: 'Safety', tier: 'primary' },
  { id: 'controller', label: 'Controle', tier: 'primary' },
  { id: 'quality', label: 'Qualidade', tier: 'primary' },
  { id: 'advanced-insights', label: 'Insights Avançados', tier: 'primary' },
  { id: 'more', label: 'Financeiro', tier: 'secondary', collapsible: true },
  { id: 'admin', label: 'Area Administrador', tier: 'secondary', collapsible: true, adminOnly: true },
];

// Optional subsection labels rendered as indented sub-headers inside a pilar
// group. Currently only Safety uses this — the retrospective-analysis items
// nest under "ANÁLISE" so the active pre/post-flight action (e-FRAT) stays
// visually separated from the analysis surfaces.
export const V2_NAV_SUBSECTIONS = {
  analysis: { label: 'Análise' },
};

export const V2_NAV_ITEMS = [
  // Ops Home
  {
    id: 'v2-home',
    label: 'Centro Operacional',
    path: '/v2/home',
    groupId: 'ops-home',
    tier: 'primary',
    icon: 'home',
    priorityDot: true,
  },

  // Planning (pre-flight setup)
  {
    id: 'v2-flights',
    label: 'Agenda de Voos',
    path: '/v2/flights',
    groupId: 'planning',
    tier: 'primary',
    icon: 'agenda',
    priorityDot: true,
  },
  {
    id: 'v2-aircraft',
    label: 'Aeronaves',
    path: '/v2/aircraft',
    groupId: 'planning',
    tier: 'primary',
    icon: 'aircraft',
    priorityDot: true,
  },
  {
    id: 'v2-pilots',
    label: 'Pilotos',
    path: '/v2/pilots',
    groupId: 'planning',
    tier: 'primary',
    icon: 'pilots',
    priorityDot: true,
  },
  {
    id: 'v2-passenger-history',
    label: 'Passageiros',
    path: '/v2/passenger-history',
    aliasPaths: ['/v2/flight-passengers', '/flight-passengers'],
    groupId: 'planning',
    tier: 'primary',
    icon: 'passengers',
  },
  {
    id: 'v2-contacts',
    label: 'Contatos Operacionais',
    path: '/v2/planning/contacts',
    groupId: 'planning',
    tier: 'primary',
    icon: 'contacts',
  },
  {
    id: 'v2-operations-docs',
    label: 'Documentos Operacionais',
    path: '/v2/operations-docs',
    groupId: 'planning',
    tier: 'primary',
    icon: 'docs',
  },
  {
    id: 'v2-contracts',
    label: 'Contratos',
    path: '/v2/contracts',
    groupId: 'planning',
    tier: 'secondary',
    icon: 'contract',
  },
  {
    id: 'v2-chartered',
    label: 'Voos Fretados',
    path: '/v2/chartered-flights',
    groupId: 'planning',
    tier: 'secondary',
    icon: 'charter',
  },

  // Safety (risk gate before execution)
  {
    id: 'v2-safety-center',
    label: 'Safety Center',
    path: '/v2/safety-center',
    groupId: 'safety',
    tier: 'primary',
    icon: 'safety',
    priorityDot: true,
  },
  // Relato confidencial (14 CFR 5.71(a)(7)). Cada página tem entrada própria em
  // api/rbac/catalog.py + roleAccess.js, propagada aos tenants existentes pela
  // migração 0466. A intake é acessível a TODOS os papéis de propósito: o
  // requisito protege empregados, e um mecânico é exatamente quem precisa dela.
  {
    id: 'v2-confidential-report',
    label: 'Relato Confidencial',
    path: '/v2/safety/confidential-report',
    groupId: 'safety',
    tier: 'secondary',
    icon: 'safety',
  },
  {
    id: 'v2-confidential-reports',
    label: 'Relatos Confidenciais',
    path: '/v2/safety/confidential-reports',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'audit',
    roles: ['admin', 'safety_officer', 'accountable_manager'],
  },
  {
    id: 'v2-safety-flow',
    label: 'Safety Flow',
    path: '/v2/safety',
    groupId: 'advanced-insights',
    tier: 'secondary',
    icon: 'safety',
    // Hidden from the sidebar by operator request (the page is still routable
    // via direct URL so existing deep links keep working).
    hidden: true,
  },
  {
    id: 'v2-frat',
    label: 'e-FRAT',
    path: '/v2/risk-assessment/frat',
    groupId: 'safety',
    tier: 'primary',
    icon: 'frat',
  },
  {
    id: 'v2-tem',
    label: 'Painel Safety',
    path: '/v2/tem',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'safety',
  },
  {
    id: 'v2-fdm',
    label: 'FDM — Dados de Voo',
    path: '/v2/fdm',
    // The retired QAR URLs redirect to FDM and must keep the same page gate.
    aliasPaths: ['/qar', '/qar/sessions'],
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'audit',
  },
  {
    id: 'v2-risk-matrix',
    label: 'Gestão de Risco',
    path: '/v2/risk-assessment/risk-matrix',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'risk',
  },
  {
    id: 'v2-hazard-approvals',
    accessPageId: 'v2-hazard-approvals',
    label: 'Aprovações de Perigos',
    path: '/v2/risk-assessment/hazard-approvals',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'audit',
    roles: ['admin', 'safety_officer', 'accountable_manager'],
  },
  {
    id: 'v2-safety-audit',
    label: 'Auditoria de Safety',
    path: '/v2/safety-audit',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'audit',
  },
  {
    id: 'v2-safety-assessment',
    label: 'Safety Assessment',
    path: '/v2/safety-assessment',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'frat',
  },
  {
    id: 'v2-erp',
    label: 'ERP',
    path: '/v2/erp',
    groupId: 'safety',
    subsection: 'analysis',
    tier: 'secondary',
    icon: 'warning',
  },
  // Controller (live mission execution)
  {
    id: 'v2-mission-control',
    label: 'Controle da Missão',
    path: '/v2/mission-control',
    groupId: 'controller',
    tier: 'primary',
    icon: 'mission',
    priorityDot: true,
  },

  // Quality (post-flight closeout)
  {
    id: 'v2-edb-closeout',
    label: 'eDB',
    path: '/v2/edb-anac',
    groupId: 'quality',
    tier: 'primary',
    icon: 'logbook',
    priorityDot: true,
  },
  {
    id: 'v2-logbook',
    label: 'Logbook Guiado',
    path: '/v2/logbook',
    groupId: 'quality',
    tier: 'primary',
    icon: 'logbook',
  },
  {
    id: 'v2-flight-logs',
    label: 'Registro de Voo - Draft',
    path: '/v2/flight-logs',
    groupId: 'quality',
    tier: 'secondary',
    icon: 'draft',
  },
  {
    id: 'v2-flight-debrief',
    label: 'Ops Debrief',
    path: '/v2/flight-debrief',
    groupId: 'quality',
    tier: 'primary',
    icon: 'debrief',
  },
  {
    id: 'v2-fuel-entry',
    label: 'Entrada de Combustível',
    path: '/v2/fuel-entry',
    groupId: 'quality',
    tier: 'primary',
    icon: 'fuel',
  },
  {
    id: 'v2-unified-fuel-entry',
    label: 'Entrada Unificada de Combustível',
    path: '/v2/unified-fuel-entry',
    groupId: 'quality',
    tier: 'secondary',
    icon: 'fuel',
  },
  {
    id: 'v2-flight-costs',
    label: 'Custos de Voo',
    path: '/v2/flight-costs',
    groupId: 'quality',
    tier: 'primary',
    icon: 'costs',
    priorityDot: true,
    // Consolidated into Financeiro; keep the permission key and direct route
    // alive for compatibility, but do not expose a second navigation entry.
    hidden: true,
  },
  {
    id: 'v2-passenger-ratings',
    label: 'Avaliação Pax',
    path: '/v2/passenger-ratings',
    groupId: 'quality',
    tier: 'secondary',
    icon: 'ratings',
  },
  // Insights & Financeiro (advanced support tools)
  {
    id: 'v2-financeiro',
    label: 'Financeiro',
    path: '/v2/financeiro',
    groupId: 'more',
    tier: 'primary',
    icon: 'budget',
    priorityDot: true,
  },
  {
    id: 'v2-safety-analysis',
    label: 'Base NTSB',
    path: '/v2/safety-analysis',
    groupId: 'advanced-insights',
    tier: 'secondary',
    icon: 'warning',
    // Hidden from the sidebar by operator request.
    hidden: true,
  },
  {
    id: 'v2-fuel-analyzer',
    label: 'Analisador de Combustível',
    path: '/v2/fuel-analyzer',
    groupId: 'more',
    tier: 'secondary',
    icon: 'fuel',
  },
  {
    id: 'v2-fixed-costs',
    label: 'Custos Fixos',
    path: '/v2/fixed-costs',
    groupId: 'more',
    tier: 'secondary',
    icon: 'fixed',
    priorityDot: true,
    hidden: true,
  },
  {
    id: 'v2-variable-costs',
    label: 'Custos Variáveis',
    path: '/v2/variable-costs',
    groupId: 'more',
    tier: 'secondary',
    icon: 'variable',
    priorityDot: true,
    hidden: true,
  },
  {
    id: 'v2-prestacao-contas',
    label: 'Cartões Corporativos',
    path: '/v2/prestacao-contas',
    groupId: 'more',
    tier: 'secondary',
    icon: 'wallet',
  },
  {
    id: 'v2-budget',
    label: 'Orçamento',
    path: '/v2/budget',
    groupId: 'more',
    tier: 'secondary',
    icon: 'budget',
    hidden: true,
  },
  {
    id: 'v2-cost-intelligence',
    label: 'Inteligência de Custos',
    path: '/v2/cost-intelligence',
    groupId: 'advanced-insights',
    tier: 'secondary',
    icon: 'intelligence',
  },
  {
    id: 'v2-accumulated-intelligence',
    label: 'Inteligência Acumulativa',
    path: '/v2/accumulated-intelligence',
    groupId: 'advanced-insights',
    tier: 'secondary',
    icon: 'intelligence',
    adminOnly: true,
  },
  {
    id: 'v2-ai-agents-quality',
    label: 'Qualidade - Agentes IA',
    path: '/operational-assurance',
    groupId: 'advanced-insights',
    tier: 'secondary',
    icon: 'intelligence',
    // Permanent page boundary mirrored from operationalAssuranceAccess (the
    // backend enforces the same allowlist independently of the tenant
    // navigation map, so this item deliberately bypasses the accessMap).
    permanentRoles: OPERATIONAL_ASSURANCE_READ_ROLES,
  },
  {
    id: 'v2-audit-report',
    label: 'Auditoria Operacional',
    path: '/v2/audit-report',
    groupId: 'advanced-insights',
    tier: 'secondary',
    icon: 'audit',
    // Hidden from the sidebar by operator request.
    hidden: true,
  },
  {
    id: 'v2-whatsapp-test',
    label: 'WhatsApp Test',
    path: '/v2/whatsapp-test',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'chat',
    adminOnly: true,
  },

  // Admin
  {
    id: 'v2-settings-team',
    label: 'Usuários e Acessos',
    path: '/v2/settings/team',
    groupId: 'admin',
    tier: 'primary',
    icon: 'team',
    adminOnly: true,
  },
  {
    id: 'v2-billing',
    label: 'Area do Assinante',
    path: '/v2/billing',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'billing',
    adminOnly: true,
  },
  {
    id: 'v2-admin-billing',
    label: 'Cobrança Admin',
    path: '/v2/admin/billing',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'billing',
    adminOnly: true,
  },
  {
    id: 'v2-admin-notifications',
    label: 'Notificações Admin',
    path: '/v2/admin/notifications',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'notifications',
    adminOnly: true,
  },
  {
    id: 'v2-admin-email-integrations',
    label: 'Integrações de Email Admin',
    path: '/v2/admin/email-integrations',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'mail',
    adminOnly: true,
  },
  {
    id: 'v2-admin-whatsapp-mission',
    label: 'Conversas WhatsApp Admin',
    path: '/v2/admin/whatsapp-mission-control',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'chat',
    adminOnly: true,
  },
  {
    id: 'v2-admin-lgpd',
    label: 'Admin LGPD',
    path: '/v2/admin/lgpd',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'security',
    adminOnly: true,
  },
  {
    id: 'v2-admin-company-goals',
    label: 'Admin Metas',
    path: '/v2/admin/company-goals',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'target',
    adminOnly: true,
  },
  {
    id: 'v2-admin-ai-feedback',
    label: 'Feedback de IA Admin',
    path: '/v2/admin/ai-feedback',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'intelligence',
    adminOnly: true,
  },
  {
    id: 'v2-admin-ai-cost',
    label: 'Gestor de Custo IA Admin',
    path: '/v2/admin/ai-cost-manager',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'intelligence',
    adminOnly: true,
    aviatoAdminOnly: true,
  },
  {
    id: 'v2-admin-account-usage',
    label: 'Consumo por Conta',
    path: '/v2/admin/account-usage',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'intelligence',
    adminOnly: true,
    aviatoAdminOnly: true,
  },
  // Lições do Assistente — fila de revisão humana do loop de aprendizado do
  // assistente. É por conta: o admin da conta decide o que entra no prompt,
  // por isso não leva `aviatoAdminOnly`.
  {
    id: 'v2-admin-assistant-lessons',
    label: 'Lições do Assistente',
    path: '/v2/admin/assistant-lessons',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'intelligence',
    adminOnly: true,
  },
  // NoD Rollout Readiness — entry kept for sidebar parity with the
  // legacy chrome. The V2 path redirects to the legacy canonical at
  // `/admin/nod-rollout-readiness` (see PR #85). Clicking from V2
  // chrome will switch the operator into legacy chrome by design.
  {
    id: 'v2-admin-nod-rollout-readiness',
    label: 'Prontidão NoD Admin',
    path: '/v2/admin/nod-rollout-readiness',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'intelligence',
    adminOnly: true,
    aviatoAdminOnly: true,
  },
  {
    id: 'v2-admin-owner',
    label: 'Painel Owner',
    path: '/v2/admin/owner',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'owner',
    adminOnly: true,
    aviatoAdminOnly: true,
  },
  {
    id: 'v2-admin-waitlist',
    label: 'Lista de Espera Admin',
    path: '/v2/admin/waitlist',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'list',
    adminOnly: true,
    aviatoAdminOnly: true,
  },
  {
    id: 'v2-admin-vip-invites',
    label: 'Convites VIP',
    path: '/v2/admin/vip-invites',
    groupId: 'admin',
    tier: 'secondary',
    icon: 'list',
    adminOnly: true,
    aviatoAdminOnly: true,
  },
];

export const V2_LEGACY_ALIASES = [
  { from: '/v2/frat', to: '/v2/risk-assessment/frat' },
  { from: '/v2/debrief', to: '/v2/flight-debrief' },
  { from: '/v2/risk-matrix', to: '/v2/risk-assessment/risk-matrix' },
  { from: '/v2/safety-flow', to: '/v2/safety' },
];

const V2_ROLE_ACCESS_PATH_ALIASES = [
  { path: '/v2', itemId: 'v2-home', exact: true },
  // Compatibility/reversal routes mount the same financial surfaces and must
  // never bypass the permission keys that protect their canonical routes.
  { path: '/budget-classic', itemId: 'v2-budget' },
  { path: '/fixed-costs-classic', itemId: 'v2-fixed-costs' },
  { path: '/variable-costs-classic', itemId: 'v2-variable-costs' },
  { path: '/flight-costs-classic', itemId: 'v2-flight-costs' },
  { path: '/v2/dashboard', itemId: 'v2-home' },
  { path: '/v2/ops-home', itemId: 'v2-home' },
  { path: '/v2/flight-request/new', itemId: 'v2-flights' },
  { path: '/v2/flight-passengers', itemId: 'v2-passenger-history' },
  { path: '/passenger-database', itemId: 'v2-passenger-history' },
  { path: '/v2/passenger-database', itemId: 'v2-passenger-history' },
  { path: '/v2/flight-checklist', itemId: 'v2-flights' },
  { path: '/v2/checklist-operacional', itemId: 'v2-flights' },
  { path: '/v2/pilots', itemId: 'v2-pilots' },
  { path: '/v2/me/calibration', itemId: 'v2-pilots' },
  { path: '/v2/safety-guide', itemId: 'v2-safety-flow' },
  { path: '/v2/safety-briefing', itemId: 'v2-tem' },
  { path: '/v2/frat/fleet-dashboard', itemId: 'v2-frat' },
  { path: '/v2/admin/whatsapp', itemId: 'v2-settings-team' },
  { path: '/v2/admin/whatsapp-intake', itemId: 'v2-admin-whatsapp-mission' },
];

function itemMatchesPath(pathname, itemPath, exact = false) {
  if (!itemPath) return false;
  if (pathname === itemPath) return true;
  return !exact && pathname.startsWith(`${itemPath}/`);
}

export function canRoleAccessV2Item(item, role, isAviatoAdmin = false, accessMap = null) {
  if (!item) return true;
  const normalizedRole = normalizeRole(role);

  if (item.aviatoAdminOnly && !isAviatoAdmin) return false;
  if (isAviatoAdmin) return true;
  // Permanent role boundaries (e.g. Operational Assurance) are enforced by the
  // backend independently of the tenant navigation map; the item's fixed
  // allowlist is authoritative and the accessMap is deliberately not consulted.
  if (Array.isArray(item.permanentRoles)) {
    return item.permanentRoles.includes(normalizedRole);
  }
  if (Array.isArray(item.roles) && !item.roles.includes(normalizedRole)) return false;
  if (item.adminOnly && normalizedRole !== 'admin') return false;

  return roleCanAccessPage(item.accessPageId || item.id, normalizedRole, accessMap);
}

export function findV2AccessItemForPath(pathname) {
  const normalizedPath = String(pathname || '').split('?')[0].replace(/\/+$/, '') || '/';
  const itemsById = new Map(V2_NAV_ITEMS.map((item) => [item.id, item]));
  const candidates = [
    ...V2_ROLE_ACCESS_PATH_ALIASES
      .flatMap((alias) => [
        { ...alias, item: itemsById.get(alias.itemId) },
        { ...alias, path: legacyPathForV2Path(alias.path), item: itemsById.get(alias.itemId) },
      ])
      .filter((alias) => alias.item),
    ...V2_NAV_ITEMS.flatMap((item) => [
      { path: item.path, item },
      { path: legacyPathForV2Path(item.path), item },
      ...(Array.isArray(item.aliasPaths)
        ? item.aliasPaths.flatMap((aliasPath) => [
          { path: aliasPath, item },
          { path: legacyPathForV2Path(aliasPath), item },
        ])
        : []),
    ]),
  ].sort((a, b) => String(b.path || '').length - String(a.path || '').length);

  return candidates.find((candidate) => (
    itemMatchesPath(normalizedPath, candidate.path, candidate.exact)
  ))?.item || null;
}

const FINANCEIRO_SHELL_PAGE_IDS = Object.freeze([
  'v2-financeiro',
  'v2-fixed-costs',
  'v2-variable-costs',
  'v2-flight-costs',
  'v2-budget',
]);

/**
 * Compatibility shell permission. Tenant maps created before Financeiro was
 * introduced remain authoritative, so any previously granted financial tab
 * may open the shell. This grants navigation only: Payables/CFO still check
 * the explicit v2-financeiro level and each tab keeps its own scope.
 */
export function canRoleAccessFinanceiroShell(role, isAviatoAdmin = false, accessMap = null) {
  if (isAviatoAdmin) return true;
  const normalizedRole = normalizeRole(role);
  return FINANCEIRO_SHELL_PAGE_IDS.some((pageId) => (
    roleCanAccessPage(pageId, normalizedRole, accessMap)
  ));
}

export function canRoleAccessV2Path(pathname, role, isAviatoAdmin = false, accessMap = null) {
  const normalizedPath = String(pathname || '');
  const item = findV2AccessItemForPath(normalizedPath);
  if (!item) return true;
  if (item.id === 'v2-financeiro') {
    return canRoleAccessFinanceiroShell(role, isAviatoAdmin, accessMap);
  }
  return canRoleAccessV2Item(item, role, isAviatoAdmin, accessMap);
}

export function getV2NavItemsForRole(role, isAviatoAdmin = false, accessMap = null) {
  // `hidden` items stay routable but never render in the sidebar.
  return V2_NAV_ITEMS.filter(
    (item) =>
      !item.hidden &&
      (item.id === 'v2-financeiro'
        ? canRoleAccessFinanceiroShell(role, isAviatoAdmin, accessMap)
        : canRoleAccessV2Item(item, role, isAviatoAdmin, accessMap))
  );
}
